import { Menu } from '@bennows/boows-react';
import { ImageUp, MessageSquareWarning, MonitorUp, PenLine } from 'lucide-react';
import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../lib/toasts';
import { useMe } from '../auth/useMe';
import { canvasFromFile, captureScreen } from './feedback-capture';
import { FeedbackDialog } from './FeedbackDialog';

const IMAGE_TYPES = 'image/png,image/jpeg,image/webp';

/**
 * The feedback button (spec 05 section 6.1, style guide 2.101): only for tenants whose
 * `active_tenant.feedback_enabled` is set. Three ways in: capture this tab, choose a
 * screenshot, or text only; the report goes to bennows.
 */
export function FeedbackButton() {
  const { t } = useTranslation();
  const toast = useToast();
  const me = useMe();
  const fileRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<{ readonly image: HTMLCanvasElement | null } | null>(null);
  const [hidden, setHidden] = useState(false);

  if (!me.data?.active_tenant?.feedback_enabled) return null;

  const capture = async () => {
    const result = await captureScreen(setHidden);
    if (result instanceof HTMLCanvasElement) setDraft({ image: result });
    else toast(t(`feedback.capture.${result.kind}`), result.kind === 'cancelled' ? 'info' : 'error');
  };

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const image = await canvasFromFile(file);
    if (image) setDraft({ image });
    else toast(t('feedback.capture.badFile'), 'error');
  };

  const onSent = () => {
    setDraft(null);
    const name = me.data?.user.display_name.split(' ')[0];
    toast(name ? t('feedback.sent', { name }) : t('feedback.sentAnonymous'));
  };

  return (
    <div className={`ui-feedback-launcher${hidden ? ' ui-feedback-launcher--hidden' : ''}`}>
      <Menu
        label={t('feedback.menuLabel')}
        triggerLabel={t('feedback.button')}
        triggerIcon={<MessageSquareWarning aria-hidden="true" />}
        variant="primary"
        align="end"
        testId="feedback-menu"
        items={[
          { id: 'capture', label: t('feedback.menu.capture'), icon: <MonitorUp aria-hidden="true" />, onSelect: () => void capture() },
          { id: 'file', label: t('feedback.menu.file'), icon: <ImageUp aria-hidden="true" />, onSelect: () => fileRef.current?.click() },
          { id: 'text', label: t('feedback.menu.text'), icon: <PenLine aria-hidden="true" />, onSelect: () => setDraft({ image: null }) },
        ]}
      />
      <input ref={fileRef} type="file" accept={IMAGE_TYPES} hidden aria-label={t('feedback.fileLabel')} onChange={(event) => void onFile(event)} /> {/* design-gate: raw-form-control — hidden file picker opened by the Menu item; boows has no headless file input */}
      {draft ? <FeedbackDialog image={draft.image} onClose={() => setDraft(null)} onSent={onSent} /> : null}
    </div>
  );
}
